/**
 * Line Drawing Animation
 * 高級感のある線画アニメーション（GSAP使用）
 *
 * フェーズ構成:
 * Phase 1: 背景フェードイン (0-0.5s)
 * Phase 2: グリッド線出現 (0.3-1.0s)
 * Phase 3: 商品輪郭描画 (0.8-3.5s)
 * Phase 4: ディテール出現 (3.0-5.0s)
 * Phase 5: グロー効果 (4.5-5.5s)
 */

(function () {
  'use strict';

  // GSAP が読み込まれているか確認
  if (typeof gsap === 'undefined') {
    console.warn('Line Drawing Animation: GSAP is not loaded. Falling back to basic animation.');
    return initBasicAnimation();
  }

  // 設定
  const CONFIG = {
    // PC用タイミング（秒）
    pc: {
      bgFadeIn: { start: 0, duration: 0.6 },
      gridFadeIn: { start: 0.3, duration: 0.7, stagger: 0.05 },
      mainDraw: { start: 0.8, duration: 2.7 },
      detailFadeIn: { start: 3.0, duration: 0.4, stagger: 0.08 },
      glowEffect: { start: 4.5, duration: 1.0 },
    },
    // SP用タイミング（短縮版）
    sp: {
      bgFadeIn: { start: 0, duration: 0.5 },
      gridFadeIn: { start: 0.2, duration: 0.5, stagger: 0.04 },
      mainDraw: { start: 0.5, duration: 2.0 },
      detailFadeIn: { start: 2.2, duration: 0.3, stagger: 0.06 },
      glowEffect: { start: 3.2, duration: 0.8 },
    },
    // Intersection Observer 設定
    observer: {
      root: null,
      rootMargin: '0px',
      threshold: 0.2,
    },
    // イージング
    easing: {
      elegant: 'power2.inOut',
      smooth: 'power1.out',
      draw: 'power2.inOut',
    },
  };

  /**
   * デバイスタイプを判定
   */
  function getDeviceType() {
    return window.innerWidth >= 769 ? 'pc' : 'sp';
  }

  /**
   * SVGをfetchして挿入
   */
  async function loadSVG(container, svgUrl) {
    try {
      const response = await fetch(svgUrl);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const svgText = await response.text();
      container.innerHTML = svgText;

      const svgElement = container.querySelector('svg');
      if (!svgElement) throw new Error('SVG element not found');

      // SVGのサイズを調整
      svgElement.style.width = '100%';
      svgElement.style.height = '100%';
      svgElement.style.display = 'block';

      return svgElement;
    } catch (error) {
      console.error('SVG読み込みエラー:', error);
      return null;
    }
  }

  /**
   * SVG要素を分類
   */
  function classifySVGElements(svgElement) {
    const paths = svgElement.querySelectorAll('path');
    const lines = svgElement.querySelectorAll('line');

    const elements = {
      mainPath: null, // メインの輪郭（最大のpath）
      detailPaths: [], // ディテール（小さなpath）
      gridLines: [], // グリッドライン
    };

    // pathを分類（d属性の長さで判定）
    let maxPathLength = 0;
    paths.forEach((path, index) => {
      const d = path.getAttribute('d') || '';

      if (d.length > maxPathLength) {
        // 以前のmainPathをdetailに移動
        if (elements.mainPath) {
          elements.detailPaths.push(elements.mainPath);
        }
        maxPathLength = d.length;
        elements.mainPath = path;
      } else {
        elements.detailPaths.push(path);
      }

      // クラス付与
      path.classList.add('svg-path');
      path.dataset.index = index;
    });

    // line要素をグリッドラインとして分類
    lines.forEach((line, index) => {
      elements.gridLines.push(line);
      line.classList.add('svg-line-grid');
      line.dataset.index = index;
    });

    return elements;
  }

  /**
   * パスの長さを計算してCSS変数に設定
   */
  function setupStrokeAnimation(path) {
    if (typeof path.getTotalLength !== 'function') return false;

    try {
      const length = path.getTotalLength();
      path.style.setProperty('--path-length', length);
      path.style.strokeDasharray = length;
      path.style.strokeDashoffset = length;

      // fillを一時的に透明に
      const originalFill = path.getAttribute('fill');
      if (originalFill && originalFill !== 'none') {
        path.dataset.originalFill = originalFill;
        path.style.fill = 'transparent';
      }

      return true;
    } catch (e) {
      console.warn('getTotalLength error:', e);
      return false;
    }
  }

  /**
   * GSAPタイムラインを作成
   */
  function createAnimationTimeline(container, bgImage, svgElements, deviceType) {
    const timing = CONFIG[deviceType];
    const tl = gsap.timeline({ paused: true });

    // Phase 1: 背景フェードイン
    if (bgImage) {
      tl.to(
        bgImage,
        {
          opacity: 1,
          duration: timing.bgFadeIn.duration,
          ease: CONFIG.easing.smooth,
        },
        timing.bgFadeIn.start
      );
    }

    // Phase 2: グリッド線出現
    if (svgElements.gridLines.length > 0) {
      tl.to(
        svgElements.gridLines,
        {
          opacity: 1,
          duration: timing.gridFadeIn.duration,
          stagger: timing.gridFadeIn.stagger,
          ease: CONFIG.easing.smooth,
        },
        timing.gridFadeIn.start
      );
    }

    // Phase 3: メイン輪郭描画
    if (svgElements.mainPath) {
      const mainPath = svgElements.mainPath;
      const hasStroke = setupStrokeAnimation(mainPath);

      if (hasStroke) {
        // ストロークアニメーション
        tl.to(
          mainPath,
          {
            opacity: 1,
            duration: 0.1,
            ease: 'none',
          },
          timing.mainDraw.start
        );

        tl.to(
          mainPath,
          {
            strokeDashoffset: 0,
            duration: timing.mainDraw.duration,
            ease: CONFIG.easing.draw,
          },
          timing.mainDraw.start
        );

        // fill を戻す
        if (mainPath.dataset.originalFill) {
          tl.to(
            mainPath,
            {
              fill: mainPath.dataset.originalFill,
              duration: 0.5,
              ease: CONFIG.easing.smooth,
            },
            timing.mainDraw.start + timing.mainDraw.duration - 0.3
          );
        }
      } else {
        // フォールバック: フェードイン
        tl.to(
          mainPath,
          {
            opacity: 1,
            duration: timing.mainDraw.duration,
            ease: CONFIG.easing.smooth,
          },
          timing.mainDraw.start
        );
      }
    }

    // Phase 4: ディテール出現
    if (svgElements.detailPaths.length > 0) {
      tl.to(
        svgElements.detailPaths,
        {
          opacity: 1,
          duration: timing.detailFadeIn.duration,
          stagger: timing.detailFadeIn.stagger,
          ease: CONFIG.easing.smooth,
        },
        timing.detailFadeIn.start
      );
    }

    // Phase 5: グロー効果
    const svgContainer = container.querySelector('.c-brands-story__main-image');
    if (svgContainer) {
      tl.call(
        () => {
          svgContainer.classList.add('animate-glow');
        },
        null,
        timing.glowEffect.start
      );

      // アニメーション完了処理
      tl.call(
        () => {
          svgContainer.classList.add('animation-complete');
          svgContainer.classList.remove('is-animated');
          svgContainer.classList.add('is-visible');
        },
        null,
        timing.glowEffect.start + timing.glowEffect.duration
      );
    }

    return tl;
  }

  /**
   * セクションのアニメーションを初期化
   */
  async function initSectionAnimation(container) {
    const svgContainer = container.querySelector('.c-brands-story__main-image');
    const bgImage = container.querySelector('.c-brands-story__bg-image');

    if (!svgContainer) return;

    const svgUrl = svgContainer.dataset.svgUrl;
    if (!svgUrl) return;

    // 既に読み込み済みの場合はスキップ
    if (svgContainer.dataset.loaded === 'true') return;
    svgContainer.dataset.loaded = 'true';

    // 初期状態のクラスを追加
    svgContainer.classList.add('is-animated');
    if (bgImage) {
      bgImage.classList.add('is-animated');
      bgImage.style.opacity = '0';
    }

    // SVGを読み込み
    const svgElement = await loadSVG(svgContainer, svgUrl);
    if (!svgElement) return;

    // SVGを非表示にしておく
    svgElement.style.opacity = '0';

    // 要素を分類
    const svgElements = classifySVGElements(svgElement);

    // 初期状態を設定
    if (svgElements.mainPath) {
      gsap.set(svgElements.mainPath, { opacity: 0 });
    }
    if (svgElements.detailPaths.length > 0) {
      gsap.set(svgElements.detailPaths, { opacity: 0 });
    }
    if (svgElements.gridLines.length > 0) {
      gsap.set(svgElements.gridLines, { opacity: 0 });
    }

    // SVGを表示
    svgElement.style.opacity = '1';

    // デバイスタイプを取得
    const deviceType = getDeviceType();

    // タイムラインを作成・実行
    const timeline = createAnimationTimeline(container, bgImage, svgElements, deviceType);
    timeline.play();
  }

  /**
   * Intersection Observer で監視を開始
   */
  function initObserver() {
    const sections = document.querySelectorAll('.c-brands-story');

    if (sections.length === 0) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          initSectionAnimation(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, CONFIG.observer);

    sections.forEach((section) => {
      const svgContainer = section.querySelector('.c-brands-story__main-image');
      if (svgContainer && svgContainer.dataset.svgUrl) {
        observer.observe(section);
      }
    });
  }

  /**
   * 基本アニメーション（GSAPなしのフォールバック）
   */
  function initBasicAnimation() {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.2,
    };

    async function basicAnimate(container) {
      const svgContainer = container.querySelector('.c-brands-story__main-image');
      const bgImage = container.querySelector('.c-brands-story__bg-image');

      if (!svgContainer) return;

      const svgUrl = svgContainer.dataset.svgUrl;
      if (!svgUrl || svgContainer.dataset.loaded === 'true') return;

      svgContainer.dataset.loaded = 'true';

      // 背景をフェードイン
      if (bgImage) {
        bgImage.style.transition = 'opacity 0.6s ease-out';
        bgImage.style.opacity = '1';
      }

      // SVGを読み込み
      try {
        const response = await fetch(svgUrl);
        const svgText = await response.text();
        svgContainer.innerHTML = svgText;

        const svgElement = svgContainer.querySelector('svg');
        if (!svgElement) return;

        svgElement.style.width = '100%';
        svgElement.style.height = '100%';
        svgElement.style.display = 'block';
        svgElement.style.opacity = '0';
        svgElement.style.transition = 'opacity 0.8s ease-out';

        // 少し遅延してからSVGを表示
        setTimeout(() => {
          svgElement.style.opacity = '1';
        }, 300);

        // パスのアニメーション
        const paths = svgElement.querySelectorAll('path, line');
        paths.forEach((path, index) => {
          if (typeof path.getTotalLength === 'function') {
            const length = path.getTotalLength();
            path.style.strokeDasharray = length;
            path.style.strokeDashoffset = length;

            setTimeout(
              () => {
                path.style.transition = 'stroke-dashoffset 0.8s ease-in-out';
                path.style.strokeDashoffset = '0';
              },
              500 + index * 100
            );
          }
        });
      } catch (error) {
        console.error('SVG読み込みエラー:', error);
      }
    }

    const sections = document.querySelectorAll('.c-brands-story');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          basicAnimate(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    sections.forEach((section) => {
      const svgContainer = section.querySelector('.c-brands-story__main-image');
      if (svgContainer && svgContainer.dataset.svgUrl) {
        observer.observe(section);
      }
    });
  }

  /**
   * テキストアニメーション（既存機能の維持）
   */
  function initTextAnimation() {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.2,
    };

    // テキストスライドインアニメーション
    const textElements = document.querySelectorAll('.c-brands-story .js-text-animate');

    const textObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const slideElements = entry.target.querySelectorAll('.js-slide-in');

          slideElements.forEach((el) => {
            const delay = parseInt(el.dataset.delay) || 0;
            setTimeout(() => {
              el.classList.add('is-visible');
            }, delay);
          });

          textObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    textElements.forEach((el) => textObserver.observe(el));

    // 本文のアニメーション
    const descElements = document.querySelectorAll('.c-brands-story__description .js-slide-in');

    const descObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const delay = parseInt(entry.target.dataset.delay) || 0;
          setTimeout(() => {
            entry.target.classList.add('is-visible');
          }, delay);

          descObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    descElements.forEach((el) => descObserver.observe(el));
  }

  /**
   * 初期化
   */
  function init() {
    // モーション軽減設定をチェック
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      // モーション軽減モード: 即座に表示
      document.querySelectorAll('.c-brands-story').forEach((container) => {
        const bgImage = container.querySelector('.c-brands-story__bg-image');
        const svgContainer = container.querySelector('.c-brands-story__main-image');

        if (bgImage) bgImage.style.opacity = '1';
        if (svgContainer) {
          svgContainer.classList.add('is-visible');
          svgContainer.classList.remove('is-animated');
        }
      });
      return;
    }

    // 通常モード
    initObserver();
    initTextAnimation();
  }

  // DOMContentLoaded で初期化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
